package com.iregained.site.components.widgets

import androidx.compose.runtime.*
import com.iregained.site.components.widgets.styles.*
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Iframe

// UNIQUE ANIMATION: Soft Card Breathe
@Composable
private fun Modifier.softBreathe(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.98f).transition(
        Transition.of("opacity", 1.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 1.s, TransitionTimingFunction.EaseOut)
    )
}

@Composable
fun YouTubeVideoCard(
    embedUrl: String,
    thumbnailUrl: String? = null,
    ariaLabel: String = "Play video",
    labelPillText: String? = "Watch",
    posterOverrideUrl: String? = null,
    videoFormat: VideoFormat = VideoFormat.STANDARD_16_9,
    isCompact: Boolean = false,
    modifier: Modifier = Modifier,
) {
    val breakpoint = LocalBreakpoint.current
    val isMobile = breakpoint < Res.Breakpoints.MD

    val isYouTube = embedUrl.contains("youtube.com/embed/")
    val isVimeo = embedUrl.contains("player.vimeo.com/video/")
    val isSafeUrl = isYouTube || isVimeo
    var isVisible by remember { mutableStateOf(false) }

    // --- RESPONSIVE SCALING ---
    // If the video format is Square (often used in small grid cards) or if on Mobile, scale the buttons down.
    val isSmallCard = videoFormat == VideoFormat.SQUARE_1_1 || isMobile || isCompact

    val playButtonSize = if (isSmallCard) 60.px else 90.px
    val playIconSize = if (isSmallCard) 20.px else 30.px
    val pulseSize = if (isSmallCard) 60.px else 90.px
    val closePadding = if (isSmallCard) Modifier.padding(topBottom = 8.px, leftRight = 16.px) else Modifier.padding(topBottom = 12.px, leftRight = 24.px)
    val closeFontSize = if (isSmallCard) 14.px else 18.px
    // --------------------------

    val containerRatio = when (videoFormat) {
        VideoFormat.CINEMATIC_ULTRAWIDE -> if (isMobile) "16 / 9" else "1920 / 600"
        VideoFormat.SQUARE_1_1 -> if (isMobile) "4 / 3" else "1 / 1"
        VideoFormat.STANDARD_4_3 -> "4 / 3"
        VideoFormat.STANDARD_16_9 -> "16 / 9"
    }

    val derivedPoster: String? = remember(embedUrl, thumbnailUrl) {
        if (embedUrl.isBlank()) null else thumbnailUrl ?: if (isYouTube) youTubeThumbnailOf(embedUrl) else null
    }

    val finalEmbedUrl = remember(embedUrl) { withAutoplayParams(embedUrl) }
    var isPlaying by remember { mutableStateOf(false) }

    Div(
        attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
            .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element); onDispose { observer.disconnect() }
                }
            }) {
        Box(modifier = YtCardWrapperStyle.toModifier().styleModifier { property("aspect-ratio", containerRatio) }
            .then(modifier).softBreathe(isVisible)) {
            when {
                !isSafeUrl -> {
                    SpanText(
                        text = "Video unavailable.",
                        modifier = Modifier.position(org.jetbrains.compose.web.css.Position.Absolute).top(50.percent)
                            .left(50.percent).translateX((-50).percent).translateY((-50).percent)
                            .color(com.varabyte.kobweb.compose.ui.graphics.Colors.White)
                    )
                }

                !isPlaying -> {
                    if (derivedPoster != null) Image(
                        src = derivedPoster, alt = "", modifier = YtPosterImageStyle.toModifier()
                    )
                    Box(modifier = YtPosterGradientStyle.toModifier())

                    if (!labelPillText.isNullOrBlank()) Div(
                        attrs = YtLabelPillStyle.toModifier().toAttrs {
                            ref { htmlElement ->
                                htmlElement.innerHTML = labelPillText
                                onDispose {}
                            }
                        })

                    // Apply the responsive scale to the Rings and Play Button
                    Box(modifier = YtPulseRingStyle.toModifier().size(pulseSize))
                    Box(modifier = YtPulseRingStyle.toModifier().size(pulseSize).styleModifier { property("animation-delay", "0.7s") })
                    Box(modifier = YtPulseRingStyle.toModifier().size(pulseSize).styleModifier { property("animation-delay", "1.4s") })

                    Box(modifier = YtPlayButtonStyle.toModifier().size(playButtonSize).onClick { isPlaying = true }) {
                        SpanText(
                            text = "▶", modifier = Modifier.fontSize(playIconSize).margin(left = 4.px)
                        )
                    }

                    Div(
                        attrs = Modifier.position(org.jetbrains.compose.web.css.Position.Absolute).top(0.px).left(0.px)
                            .fillMaxSize().onClick { isPlaying = true }
                            .toAttrs { attr("role", "button"); attr("tabindex", "0"); attr("aria-label", ariaLabel) })
                }

                else -> {
                    Iframe(
                        attrs = YtIframeStyle.toModifier().toAttrs {
                            attr("src", finalEmbedUrl); attr("title", ariaLabel); attr(
                            "loading", "lazy"
                        ); attr("allowfullscreen", "true"); attr("frameborder", "0"); attr(
                            "referrerpolicy", "strict-origin-when-cross-origin"
                        ); attr(
                            "allow",
                            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        )
                        })

                    Box(
                        modifier = Modifier.position(org.jetbrains.compose.web.css.Position.Absolute)
                            .top(if(isSmallCard) 10.px else 20.px)
                            .right(if(isSmallCard) 10.px else 20.px).zIndex(10)
                            .styleModifier {
                                property(
                                    "background-color",
                                    "rgba(0, 0, 0, 0.85)"
                                )
                            }
                            .boxShadow(
                                0.px,
                                4.px,
                                12.px,
                                0.px,
                                com.varabyte.kobweb.compose.ui.graphics.Colors.Black.copyf(alpha = 0.5f)
                            )
                            .color(com.varabyte.kobweb.compose.ui.graphics.Colors.White).border(
                                1.px,
                                LineStyle.Solid,
                                com.varabyte.kobweb.compose.ui.graphics.Colors.White.copyf(alpha = 0.3f)
                            )
                            .then(closePadding) // Applies the responsive padding
                            .borderRadius(30.px).cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                            .transition(Transition.of("background-color", 0.2.s))
                            .onClick { isPlaying = false },
                        contentAlignment = com.varabyte.kobweb.compose.ui.Alignment.Center
                    ) {
                        SpanText(
                            text = "Close ✕", modifier = Modifier.fontSize(closeFontSize) // Applies responsive font size
                                .fontWeight(FontWeight.Bold)
                        )
                    }
                }
            }
        }
    }
}

enum class VideoFormat {
    STANDARD_16_9, STANDARD_4_3, CINEMATIC_ULTRAWIDE, SQUARE_1_1
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

private fun youTubeThumbnailOf(embedUrl: String): String? {
    val marker = "youtube.com/embed/"
    val idx = embedUrl.indexOf(marker)
    if (idx < 0) return null
    val rawId = embedUrl.substring(idx + marker.length)
    val cleanId = rawId.substringBefore('?').substringBefore('/').trim()
    if (cleanId.isEmpty()) return null
    return "https://i.ytimg.com/vi/$cleanId/maxresdefault.jpg"
}

private fun withAutoplayParams(embedUrl: String): String {
    val extra = "autoplay=1&rel=0&modestbranding=1&playsinline=1&vq=hd1080"
    val separator = if (embedUrl.contains("?")) "&" else "?"
    return embedUrl + separator + extra
}